import { TailSpin } from 'react-loader-spinner'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useAtomValue } from 'jotai'
import { Blue300 } from '../../lib/colors'
import { TestItem } from '../../components/TestItem'
import { SearchTestFragment } from '../../generated/urql.client'
import {
  ConsultationFragment,
  TrainingFragment,
} from '../../generated/urql.anonymous'
import {
  BasketContext,
  isConsultationBasketItem,
  isTestBasketItem,
  isTrainingBasketItem,
} from '../../provider/basket'
import { useI18n } from '../../provider/i18n'
import { AuthContext } from '../../provider/auth'
import ProfileSelectorModal, {
  ProfileSelectorModalType,
} from '../../components/ProfileSelectorModal'
import { ConsultationItem } from '../../components/ConsultationItem'
import { TrainingItem } from '../../components/TrainingItem'
import {
  availableTestsMostPopularSubscriptionAtom,
  clientFavouriteTestsSubscriptionAtom,
  consultationsSubscriptionAtom,
  profilesSubscriptionAtom,
  trainingsSubscriptionAtom,
} from '../../store/subscription.atoms'

type Nameable = { name?: string | null }

const getFilteredData = <T extends Nameable>(
  data: T[],
  searchQuery: string
) => {
  const norm = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
  const q = norm(searchQuery.trim())
  if (!q) return data
  return data.filter((item) => item.name && norm(item.name).includes(q))
}

export const CatalogIndexPage: React.FC<{}> = () => {
  const [isInited, setIsInited] = useState(false)
  const basket = useContext(BasketContext)
  const auth = useContext(AuthContext)
  const i18n = useI18n()
  const [{ data: mostPopularTestsData, fetching: mostPopularTestsFetching }] =
    useAtomValue(availableTestsMostPopularSubscriptionAtom)
  const [{ data: consultationsData, fetching: consultationsFetching }] =
    useAtomValue(consultationsSubscriptionAtom)
  const [{ data: trainingsData, fetching: trainingsFetching }] = useAtomValue(
    trainingsSubscriptionAtom
  )
  const [{ data: profilesData }] = useAtomValue(profilesSubscriptionAtom)
  const [
    { data: clientFavouriteTestsData, fetching: clientFavouriteTestsFetching },
  ] = useAtomValue(clientFavouriteTestsSubscriptionAtom)
  const profileSelectorModalRef = useRef<ProfileSelectorModalType>(null)

  const mostPopularTests = mostPopularTestsData?.search_test_most_popular || []
  const clientFavouriteTests =
    clientFavouriteTestsData?.client_favourite_test.map(({ test }) => test) ||
    []

  const consultations = consultationsData?.consultation || []

  const trainings = trainingsData?.training || []

  const isFetching = useMemo(
    () =>
      (mostPopularTestsFetching ||
        consultationsFetching ||
        trainingsFetching ||
        clientFavouriteTestsFetching) &&
      !isInited,
    [
      mostPopularTestsFetching,
      consultationsFetching,
      trainingsFetching,
      clientFavouriteTestsFetching,
      isInited,
    ]
  )

  useEffect(() => {
    if (!isFetching) {
      setIsInited(true)
    }
  }, [isFetching])

  const onTestAddClick = useCallback(
    (test: SearchTestFragment) => {
      const profilesCount = profilesData?.client_profile.length || 0

      if (!auth.isLogged || profilesCount === 0) {
        basket.addItem({ test })
      } else if (profilesCount === 1) {
        basket.addItem({ test, profile: profilesData?.client_profile[0] })
      } else {
        profileSelectorModalRef.current?.show({
          onChange: (profile) => {
            basket.addItem({ test, profile })
          },
          filter: (profile) =>
            !basket.items.find(
              (item) =>
                isTestBasketItem(item) &&
                item.test.id === test.id &&
                item.profile?.id === profile.id
            ),
        })
      }
    },
    [auth.isLogged, basket, profilesData?.client_profile]
  )

  const onConsultationAddClick = useCallback(
    (consultation: ConsultationFragment) => {
      const profilesCount = profilesData?.client_profile.length || 0

      if (!auth.isLogged || profilesCount === 0) {
        basket.addItem({ consultation })
      } else if (profilesCount === 1) {
        basket.addItem({
          consultation,
          profile: profilesData?.client_profile[0],
        })
      } else {
        profileSelectorModalRef.current?.show({
          onChange: (profile) => {
            basket.addItem({ consultation, profile })
          },
          filter: (profile) =>
            !basket.items.find(
              (item) =>
                isConsultationBasketItem(item) &&
                item.consultation.id === consultation.id &&
                item.profile?.id === profile.id
            ),
        })
      }
    },
    [auth.isLogged, basket, profilesData?.client_profile]
  )

  const onTrainingAddClick = useCallback(
    (training: TrainingFragment) => {
      basket.addItem({ training })
    },
    [basket]
  )

  if (isFetching) {
    return (
      <TailSpin
        color={Blue300}
        height={32}
        width={32}
        wrapperClass="flex justify-center p-8"
      />
    )
  }

  return (
    <div className="mb-16">
      {consultations.length > 0 && (
        <>
          <div className="text-blue-700 my-5 flex items-center gap-1">
            {i18n.t('screens.catalog.index.consultations.header')}
          </div>
          <ul className="md:mx-5 flex flex-col gap-5 mb-10">
            {consultations.map((consultation) => (
              <li key={consultation.id}>
                <ConsultationItem
                  consultation={consultation}
                  onConsultationAddClick={() =>
                    onConsultationAddClick(consultation)
                  }
                  showAddButton={
                    auth.isLogged
                      ? basket.items
                          .filter(isConsultationBasketItem)
                          .filter(
                            ({ consultation: { id } }) => consultation.id === id
                          ).length < (profilesData?.client_profile.length || 0)
                      : !basket.items.find(
                          (item) =>
                            isConsultationBasketItem(item) &&
                            consultation.id === item.consultation.id
                        )
                  }
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {trainings.length > 0 && (
        <>
          <div className="text-blue-700 my-5 flex items-center gap-1">
            {i18n.t('screens.catalog.index.trainings.header')}
          </div>
          <ul className="md:mx-5 flex flex-col gap-5 mb-10">
            {trainings.map((training) => (
              <li key={training.id}>
                <TrainingItem
                  training={training}
                  onTrainingAddClick={() => onTrainingAddClick(training)}
                  showAddButton={
                    !training.videoFile &&
                    !basket.items.find(
                      (item) =>
                        isTrainingBasketItem(item) &&
                        training.id === item.training.id
                    )
                  }
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {basket.address?.location && mostPopularTests.length > 0 && (
        <>
          <div className="text-blue-700 my-5 flex items-center gap-1">
            {i18n.t('screens.catalog.index.mostPopular.header')}
          </div>
          <ul className="md:mx-5 flex flex-col gap-5 mb-10">
            {mostPopularTests.map((test) => (
              <li key={test.id}>
                <TestItem
                  test={test}
                  variant="green"
                  onTestAddClick={() => onTestAddClick(test)}
                  showAddButton={
                    auth.isLogged
                      ? basket.items
                          .filter(isTestBasketItem)
                          .filter(({ test: { id } }) => test.id === id).length <
                        (profilesData?.client_profile.length || 0)
                      : !basket.items.find(
                          (item) =>
                            isTestBasketItem(item) && test.id === item.test.id
                        )
                  }
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {clientFavouriteTests.length > 0 && (
        <>
          <div className="text-blue-700 mb-5 flex items-center gap-1">
            {i18n.t('screens.catalog.index.favourite.header')}
            {clientFavouriteTestsFetching && (
              <TailSpin color={Blue300} height={16} width={16} />
            )}
          </div>
          <ul className="md:mx-5 flex flex-col gap-5 mb-10">
            {clientFavouriteTests.map((test) => (
              <li key={test.id}>
                <TestItem
                  test={test}
                  onTestAddClick={() => onTestAddClick(test)}
                  showAddButton={
                    auth.isLogged
                      ? basket.items
                          .filter(isTestBasketItem)
                          .filter(({ test: { id } }) => test.id === id).length <
                        (profilesData?.client_profile.length || 0)
                      : !basket.items.find(
                          (item) =>
                            isTestBasketItem(item) && test.id === item.test.id
                        )
                  }
                />
              </li>
            ))}
          </ul>
        </>
      )}

      <ProfileSelectorModal ref={profileSelectorModalRef} />
    </div>
  )
}
